import { useRef, useState } from "react"

// ** Third Party
import { CaretSortIcon, CheckIcon, Cross2Icon } from "@radix-ui/react-icons"

// ** Hooks & Helpers
import { cn } from "@pma/utils/helpers"
import useTranslator from "@pma/hooks/use-translator"

// ** Types
import { MultiSelectDropdownProps } from "@pma/types"
import { SelectionCheckbox } from "@pma/components/FilesFolder/SelectionCheckbox"

export const MultiSelectDropdown = ({
  options,
  selected,
  onToggle,
  onClear,
  placeholder,
  disabled,
  selectedLabelFormatter,
  variant = "default",
}: MultiSelectDropdownProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { translate } = useTranslator()

  // Normalize id comparison so callers passing number ids work alongside
  // form state that stores string ids (and vice-versa).
  const isSelected = (id: string | number) => selected.some((s) => String(s) === String(id))

  const selectedItems = options.filter((o) => isSelected(o.id))

  const label =
    selected.length === 0
      ? placeholder
      : selectedLabelFormatter
        ? selectedLabelFormatter(selectedItems)
        : selected.length === 1
          ? selectedItems[0]?.name ?? placeholder
          : `${selected.length} ${translate("selected")}`

  return (
    <div ref={ref} className='relative w-full'>
      <button
        type='button'
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={cn(
          "flex w-full items-center justify-between rounded-md border bg-transparent px-3 py-[9px] text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60",
          open ? "border-indigo-400 text-neutral-500" : "border-input",
          selected.length > 0 && "bg-[#F5F8FFFC]",
        )}
      >
        <span
          className={cn(
            "line-clamp-1 text-left font-[inter] text-sm font-medium text-neutral-800",
            selected.length === 0 && "text-muted-foreground",
          )}
        >
          {label}
        </span>
        <div className='flex items-center gap-1'>
          {selected.length > 0 && !disabled && (
            <span
              role='button'
              onClick={(e) => {
                e.stopPropagation()
                onClear()
              }}
              className='rounded p-0.5 text-neutral-400 hover:text-neutral-700'
            >
              <Cross2Icon className='h-3 w-3' />
            </span>
          )}
          <CaretSortIcon className='h-4 w-4 opacity-50' />
        </div>
      </button>

      {open && (
        <>
          <div className='fixed inset-0 z-40' onClick={() => setOpen(false)} />
          <div className='absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-white p-1 shadow-md'>
            {options.length === 0 ? (
              <p className='px-3 py-2 text-xs text-neutral-400'>{translate("No options")}</p>
            ) : (
              options.map((opt) => {
                const active = isSelected(opt.id)
                if (variant === "checkbox") {
                  return (
                    <button
                      key={opt.id}
                      type='button'
                      onClick={() => onToggle(opt.id)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors",
                        active ? "bg-[#F5F8FFFC]" : "hover:bg-accent",
                      )}
                    >
                      <SelectionCheckbox checked={active} />
                      <span className='flex min-w-0 items-baseline gap-2'>
                        <span className='truncate font-[inter] font-medium text-neutral-800'>
                          {opt.name}
                        </span>
                        {opt.subtitle && (
                          <span className='truncate font-[inter] text-sm text-neutral-400'>
                            {opt.subtitle}
                          </span>
                        )}
                      </span>
                    </button>
                  )
                }
                return (
                  <button
                    key={opt.id}
                    type='button'
                    onClick={() => onToggle(opt.id)}
                    className={cn(
                      "flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors",
                      active ? "bg-[#F5F8FFFC]" : "hover:bg-accent",
                    )}
                  >
                    <span className='flex min-w-0 items-baseline gap-2'>
                      <span className='truncate font-[inter] font-semibold text-neutral-800'>
                        {opt.name}
                      </span>
                      {opt.subtitle && (
                        <span className='truncate font-[inter] text-sm text-neutral-400'>
                          {opt.subtitle}
                        </span>
                      )}
                    </span>
                    {active && <CheckIcon className='h-4 w-4 shrink-0 text-purple-500' />}
                  </button>
                )
              })
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default MultiSelectDropdown
